import { useEffect } from "react";
import { motion } from "framer-motion";
import { Target, Users, TrendUp, Globe, Medal, HandHeart } from "@phosphor-icons/react";
import NorthAmericaMap from "@/components/NorthAmericaMap";
import StatsCounter from "@/components/StatsCounter";
import CTASection from "@/components/CTASection";
import PageHero from "@/components/PageHero";
import { IMG } from "@/lib/images";

const VALUES = [
    { icon: Target, title: "Precision first", desc: "Every drawing peer-reviewed against a 40-point QA checklist before release." },
    { icon: Users, title: "42+ specialist drafters", desc: "Senior CAD technicians and licensed surveyors working exclusively on your deliverables." },
    { icon: TrendUp, title: "Scalable capacity", desc: "From a single boundary survey to 400-acre corridor projects — capacity never slows you down." },
    { icon: Globe, title: "Local expertise", desc: "State-specific coordinate systems, recording standards, and FEMA compliance across 24 states." },
    { icon: Medal, title: "Standards-compliant", desc: "2021 ALTA/NSPS, USACE, NFIP — our team lives inside the spec books." },
    { icon: HandHeart, title: "Partnerships, not jobs", desc: "70% of clients return within 90 days. We earn long-term partnerships, not single projects." },
];

const TEAM = [
    { name: "Senior CAD Lead", role: "40+ years combined", count: "8 specialists" },
    { name: "Licensed Surveyors", role: "PLS / RLS credentials", count: "6 professionals" },
    { name: "Civil 3D Experts", role: "Surface & corridor specialists", count: "12 drafters" },
    { name: "Point Cloud / BIM", role: "LiDAR + Revit modeling", count: "9 technicians" },
    { name: "QA / QC Engineers", role: "40-point review checklist", count: "4 reviewers" },
    { name: "Project Coordinators", role: "Client delivery & comms", count: "3 coordinators" },
];

export default function About() {
    useEffect(() => {
        document.title = "Surveys2CAD | About";
    }, []);

    return (
        <div>
            <PageHero variant="about" tint="violet">
                <div className="max-w-7xl mx-auto px-5 lg:px-8 pt-16 md:pt-24 pb-16 grid grid-cols-1 lg:grid-cols-5 gap-12 items-start">
                    <div className="lg:col-span-3">
                        <div className="overline mb-3">About Surveys2CAD</div>
                        <h1 className="font-heading text-5xl sm:text-6xl font-bold text-slate-50 tracking-tighter leading-[1.02]">
                            Ten years of <br />
                            <span
                                className="bg-clip-text text-transparent"
                                style={{ backgroundImage: "linear-gradient(120deg, #a855f7 0%, #22d3ee 50%, #fb923c 100%)" }}
                            >
                                obsession with precision.
                            </span>
                        </h1>
                        <p className="text-lg text-slate-400 mt-6 leading-relaxed max-w-xl">
                            Surveys2CAD was founded by licensed surveyors who got tired of CAD drafts coming back wrong. We
                            built a dedicated drafting studio that thinks like a surveyor — reads field data like a surveyor —
                            and delivers like an engineering partner. Ten years, 330,000+ drawings, 24 northern states
                            served.
                        </p>
                    </div>
                    <div className="lg:col-span-2">
                        <motion.img
                            src={IMG.surveyorField}
                            alt="Surveys2CAD field team"
                            initial={{ opacity: 0, x: 30 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.6 }}
                            className="w-full rounded-2xl border border-slate-800 object-cover aspect-[4/5]"
                        />
                    </div>
                </div>
            </PageHero>

            <section className="max-w-7xl mx-auto px-5 lg:px-8 pb-16">
                <StatsCounter />
            </section>

            <section className="bg-section-violet section-divider relative" data-testid="values-section">
                <div className="max-w-7xl mx-auto px-5 lg:px-8 py-20">
                    <div className="mb-12">
                        <div className="overline mb-3">What we stand for</div>
                        <h2 className="font-heading text-4xl md:text-5xl font-semibold text-slate-50 tracking-tight">
                            Six principles that <span className="text-cyan-400">run our studio.</span>
                        </h2>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                        {VALUES.map((v, i) => (
                            <motion.div
                                key={v.title}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: (i % 3) * 0.08 }}
                                className="tech-card p-6"
                            >
                                <v.icon size={28} weight="duotone" className="text-cyan-300 mb-4" />
                                <h3 className="font-heading text-lg font-semibold text-slate-50 mb-2">{v.title}</h3>
                                <p className="text-sm text-slate-400 leading-relaxed">{v.desc}</p>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            <section className="bg-section-emerald section-divider relative">
                <div className="max-w-7xl mx-auto px-5 lg:px-8 py-20">
                    <div className="grid grid-cols-1 lg:grid-cols-5 gap-10 items-start">
                        <div className="lg:col-span-2">
                            <div className="overline mb-3">Our team</div>
                            <h2 className="font-heading text-4xl md:text-5xl font-semibold text-slate-50 tracking-tight leading-[1.05]">
                                42+ specialists. <br />
                                <span className="text-cyan-400">One standard.</span>
                            </h2>
                            <p className="text-slate-400 mt-5 leading-relaxed">
                                Every team member trained, reviewed, and mentored by senior licensed surveyors. No outsourcing.
                                No off-shoring your deliverables.
                            </p>
                        </div>
                        <div className="lg:col-span-3 grid grid-cols-1 sm:grid-cols-2 gap-4">
                            {TEAM.map((t) => (
                                <div key={t.name} className="tech-card p-5">
                                    <div className="overline">{t.count}</div>
                                    <div className="font-heading text-base font-semibold text-slate-50 mt-1">{t.name}</div>
                                    <div className="text-xs text-slate-400 mt-1 font-mono-alt">{t.role}</div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            <section className="bg-section-amber section-divider relative">
                <div className="max-w-7xl mx-auto px-5 lg:px-8 py-20">
                    <div className="overline mb-3">Coverage & volume</div>
                    <h2 className="font-heading text-4xl md:text-5xl font-semibold text-slate-50 tracking-tight mb-3">
                        Where we ship drawings.
                    </h2>
                    <p className="text-slate-400 max-w-2xl mb-8">
                        330,000+ drawings delivered across USA and Canada — with highest volumes in Florida, Illinois,
                        Texas, Georgia, Maryland, and Ohio. Every region served by drafters familiar with local coordinate
                        systems, recording standards, and FEMA flood map panels.
                    </p>
                    <NorthAmericaMap />
                </div>
            </section>

            <section className="max-w-7xl mx-auto px-5 lg:px-8 pb-20 pt-12">
                <CTASection title="Let's make your next project the easiest one you ship." />
            </section>
        </div>
    );
}
